const isServer = typeof window === 'undefined';

let asyncLocalStorage;
let pino;
let pinoHttp;
let stdSerializers;
let cryptoModule;

const noop = () => {};
const fallbackLogger = {
  createRequestLoggerMiddleware: () => (req, res, next) => next(),
  debug: noop,
  info: noop,
  warn: noop,
  error: noop,
  perf: noop,
};

let pathModule;
let fsModule;

if (isServer) {
  try {
    // Use eval('require') to keep server-only dependencies out of client bundles.
    // eslint-disable-next-line no-eval
    const requireFunc = eval('require');
    const { AsyncLocalStorage } = requireFunc('async_hooks');
    pino = requireFunc('pino');
    pinoHttp = requireFunc('pino-http');
    stdSerializers = requireFunc('pino-std-serializers');
    cryptoModule = requireFunc('crypto');
    pathModule = requireFunc('path');
    fsModule = requireFunc('fs');
    asyncLocalStorage = new AsyncLocalStorage();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(
      `[logger] Dependency load failed — logging unavailable: ${
        error && error.message ? error.message : error
      }`,
    );
  }
}

const hasPino = isServer && pino && pinoHttp;

const baseMeta = {};

const redactPaths = [
  'req.headers.authorization',
  'req.headers.cookie',
  'req.headers["set-cookie"]',
  'headers.authorization',
  'headers.cookie',
  'headers["set-cookie"]',
  'config.headers.authorization',
  'config.headers.cookie',
  'request.headers.authorization',
  'request.headers.cookie',
];

// Log configs: set by server before first log write; fallback reads from config file.
// _logConfig        → app.log  (errors, perf, axios, business logic)
// _requestLogConfig → request.log (HTTP request lifecycle events)
let _logConfig = null;
let _requestLogConfig = null;

function getConfig(cached, configKey) {
  if (cached && typeof cached === 'object') {
    return cached;
  }
  if (!isServer) {
    return {};
  }
  try {
    // eslint-disable-next-line no-eval
    const cfg = eval('require')('../config');
    return cfg[configKey] || {};
  } catch (e) {
    return {};
  }
}

const getLogConfig = () => getConfig(_logConfig, 'log');
const getRequestLogConfig = () => getConfig(_requestLogConfig, 'requestLog');

/**
 * Call from server with config.log and config.requestLog before the first log write.
 * This ensures the build-time config path is used instead of require('../config').
 */
function setLogConfig(appLogConfig, requestLogConfig) {
  _logConfig =
    appLogConfig && typeof appLogConfig === 'object' ? appLogConfig : null;
  _requestLogConfig =
    requestLogConfig && typeof requestLogConfig === 'object'
      ? requestLogConfig
      : null;
}

// Returns { stream, actualPath } where actualPath is the resolved file path
// when writing to a file, or null when falling back to stdout. Every fallback
// branch emits a stderr line explaining the reason so it is never silent.
// Log rotation is handled entirely by the server-side logrotate daemon —
// no rotation logic lives in application code.
function createLogDestination(cfg) {
  const target = ((cfg && cfg.target) || 'console').toLowerCase();

  if (target !== 'file') {
    // eslint-disable-next-line no-console
    console.log(
      `[logger] target="${target}" (not "file") — writing to stdout. Set LOG_TARGET / REQUEST_LOG_TARGET=file to enable file logging.`,
    );
    return {
      stream: pino.destination({ dest: 1, sync: false }),
      actualPath: null,
    };
  }

  if (!pathModule || !fsModule) {
    // eslint-disable-next-line no-console
    console.log(
      '[logger] Cannot write to file — required modules (path/fs) are unavailable. Falling back to stdout.',
    );
    return {
      stream: pino.destination({ dest: 1, sync: false }),
      actualPath: null,
    };
  }

  const pathRaw = (cfg && cfg.path) || 'logs/app.log';
  const resolvedPath = pathModule.resolve(process.cwd(), pathRaw);
  const dir = pathModule.dirname(resolvedPath);

  try {
    fsModule.mkdirSync(dir, { recursive: true });
  } catch (err) {
    const errMsg = err && err.message ? err.message : String(err);
    // eslint-disable-next-line no-console
    console.log(
      `[logger] Failed to create log dir "${dir}": ${errMsg}. Falling back to stdout.`,
    );
    return {
      stream: pino.destination({ dest: 1, sync: false }),
      actualPath: null,
    };
  }

  return {
    stream: pino.destination({ dest: resolvedPath, sync: false }),
    actualPath: resolvedPath,
  };
}

let logDestination = null;
let baseLogger = null;
let requestLogDestination = null;
let requestBaseLogger = null;

function buildPinoLogger(cfg, destination) {
  return pino(
    {
      level: cfg.level || process.env.LOG_LEVEL || 'info',
      base: baseMeta,
      timestamp: pino.stdTimeFunctions.isoTime,
      messageKey: 'message',
      formatters: {
        level: label => ({ level: label }),
      },
      redact: {
        paths: redactPaths,
        remove: true,
      },
    },
    destination,
  );
}

function getBaseLogger() {
  if (baseLogger) return baseLogger;
  if (!hasPino) return null;
  const logConfig = getLogConfig();
  const { stream, actualPath } = createLogDestination(logConfig);
  logDestination = stream;
  baseLogger = buildPinoLogger(logConfig, logDestination);
  // eslint-disable-next-line no-console
  console.log(
    `[logger] App logger ready → ${actualPath || 'stdout (fallback)'}`,
  );
  return baseLogger;
}

function getRequestBaseLogger() {
  if (requestBaseLogger) return requestBaseLogger;
  if (!hasPino) return null;
  const cfg = getRequestLogConfig();
  const { stream, actualPath } = createLogDestination(cfg);
  requestLogDestination = stream;
  requestBaseLogger = buildPinoLogger(cfg, requestLogDestination);
  // eslint-disable-next-line no-console
  console.log(
    `[logger] Request logger ready → ${actualPath || 'stdout (fallback)'}`,
  );
  return requestBaseLogger;
}

const getRequestContext = () => {
  if (!asyncLocalStorage) {
    return {};
  }
  return asyncLocalStorage.getStore() || {};
};

const runWithContext = (context, fn) => {
  if (!asyncLocalStorage) {
    return fn();
  }
  return asyncLocalStorage.run(context || {}, fn);
};

const runWithId = (requestId, fn) =>
  runWithContext(
    {
      ...getRequestContext(),
      request_id: requestId,
    },
    fn,
  );

const getRequestId = () => getRequestContext().request_id || null;

const sanitizeError = error => {
  if (!error) {
    return undefined;
  }
  if (error instanceof Error) {
    // Axios errors carry verbose internal objects (config with httpAgent/httpsAgent
    // socket pools, and request with all Node.js _* internals). Extract only what
    // is actionable for debugging.
    if (error.isAxiosError || error.config || error.request) {
      const sanitized = {
        type: error.name || 'Error',
        message: error.message,
        stack: error.stack,
      };
      if (error.code !== undefined) sanitized.code = error.code;
      if (error.errno !== undefined) sanitized.errno = error.errno;
      if (error.syscall !== undefined) sanitized.syscall = error.syscall;
      if (error.hostname !== undefined) sanitized.hostname = error.hostname;
      if (error.response) {
        sanitized.response = {
          status: error.response.status,
          statusText: error.response.statusText,
          url: error.response.config && error.response.config.url,
        };
      }
      return sanitized;
    }
    return stdSerializers && stdSerializers.err
      ? stdSerializers.err(error)
      : {
          type: error.name,
          message: error.message,
          stack: error.stack,
        };
  }
  // Plain object errors (non-Error instances) — strip axios internals if present
  if (typeof error === 'object' && (error.config || error.request)) {
    const sanitized = { type: 'Error' };
    if (error.code !== undefined) sanitized.code = error.code;
    if (error.message !== undefined) sanitized.message = error.message;
    if (error.stack !== undefined) sanitized.stack = error.stack;
    if (error.errno !== undefined) sanitized.errno = error.errno;
    if (error.syscall !== undefined) sanitized.syscall = error.syscall;
    if (error.hostname !== undefined) sanitized.hostname = error.hostname;
    if (error.response) {
      sanitized.response = {
        status: error.response.status,
        statusText: error.response.statusText,
      };
    }
    return sanitized;
  }
  return error;
};

const normalizeContext = context => {
  if (!context || typeof context !== 'object') {
    return {};
  }
  return context;
};

const writeLog = (level, message, context = {}) => {
  if (!isServer) {
    return;
  }
  const requestContext = getRequestContext();
  const payload = {
    ...requestContext,
    ...normalizeContext(context),
  };
  const loggerInstance = getBaseLogger();
  if (loggerInstance) {
    loggerInstance[level](payload, message);
  }
};

const pinoLogger = {
  runWithId,
  runWithContext,
  getRequestId,
  getRequestContext,
  child: bindings => {
    const base = getBaseLogger();
    if (!base) {
      return {
        debug: noop,
        info: noop,
        warn: noop,
        error: noop,
        perf: noop,
      };
    }
    const childPino = base.child({
      ...bindings,
    });
    return {
      debug: (message, context) =>
        childPino.debug(normalizeContext(context), message),
      info: (message, context) =>
        childPino.info(normalizeContext(context), message),
      warn: (message, context) =>
        childPino.warn(normalizeContext(context), message),
      error: (message, error, context = {}) =>
        childPino.error(
          { ...normalizeContext(context), error: sanitizeError(error) },
          message,
        ),
      perf: (message, durationMs, context = {}) =>
        childPino.info(
          {
            ...normalizeContext(context),
            duration_ms: durationMs,
            event: 'performance',
          },
          message,
        ),
    };
  },
  debug: (message, context) => writeLog('debug', message, context),
  info: (message, context) => writeLog('info', message, context),
  warn: (message, context) => writeLog('warn', message, context),
  error: (message, error, context = {}) =>
    writeLog('error', message, {
      ...normalizeContext(context),
      error: sanitizeError(error),
    }),
  perf: (message, durationMs, context = {}) =>
    writeLog('info', message, {
      ...normalizeContext(context),
      event: 'performance',
      duration_ms: durationMs,
    }),
  timeSync: (label, fn, context = {}) => {
    const start = Date.now();
    try {
      const result = fn();
      const durationMs = Date.now() - start;
      writeLog('info', `${label} completed`, {
        ...normalizeContext(context),
        event: 'performance',
        duration_ms: durationMs,
      });
      return result;
    } catch (error) {
      const durationMs = Date.now() - start;
      writeLog('error', `${label} failed`, {
        ...normalizeContext(context),
        event: 'performance',
        duration_ms: durationMs,
        error: sanitizeError(error),
      });
      throw error;
    }
  },
  timeAsync: async (label, promiseOrFn, context = {}) => {
    const start = Date.now();
    try {
      const result =
        typeof promiseOrFn === 'function'
          ? await promiseOrFn()
          : await promiseOrFn;
      const durationMs = Date.now() - start;
      writeLog('info', `${label} completed`, {
        ...normalizeContext(context),
        event: 'performance',
        duration_ms: durationMs,
      });
      return result;
    } catch (error) {
      const durationMs = Date.now() - start;
      writeLog('error', `${label} failed`, {
        ...normalizeContext(context),
        event: 'performance',
        duration_ms: durationMs,
        error: sanitizeError(error),
      });
      throw error;
    }
  },
  createRequestLoggerMiddleware: () => {
    const httpLogger = pinoHttp({
      logger: getRequestBaseLogger(),
      autoLogging: false,
      genReqId: req => {
        const existing =
          req.headers['x-request-id'] || req.headers['x-requestid'];
        if (existing) {
          return existing;
        }
        if (cryptoModule && typeof cryptoModule.randomUUID === 'function') {
          return cryptoModule.randomUUID();
        }
        return `req-${Date.now()}-${Math.round(Math.random() * 1000000)}`;
      },
      customAttributeKeys: {
        req: 'request',
        res: 'response',
        responseTime: 'response_time_ms',
        err: 'error',
      },
      // Strip the verbose raw request binding pino-http attaches to every
      // req.log.* call. We already log all relevant fields (method, url, etc.)
      // explicitly, so the duplicate nested object is pure noise.
      serializers: {
        request: () => undefined,
        response: () => undefined,
      },
    });

    return (req, res, next) => {
      httpLogger(req, res);
      const requestId = req.id || req.headers['x-request-id'];
      res.setHeader('X-Request-Id', requestId);
      const startTime = Date.now();

      runWithContext(
        {
          request_id: requestId,
          request_path: req.path,
          request_method: req.method,
        },
        () => {
          const requestStartMeta = {
            event: 'request_start',
            request_id: requestId,
            method: req.method,
            url: req.originalUrl,
          };

          const userAgent = req.get('user-agent');
          if (userAgent) requestStartMeta.user_agent = userAgent;
          const referer = req.get('referer');
          if (referer) requestStartMeta.referer = referer;
          // eslint-disable-next-line operator-linebreak
          const remoteAddress =
            req.headers['x-forwarded-for'] || req.socket.remoteAddress || null;
          if (remoteAddress) requestStartMeta.remote_address = remoteAddress;
          req.log.info(requestStartMeta, 'Incoming request');

          res.on('finish', () => {
            const durationMs = Date.now() - startTime;
            const level =
              // eslint-disable-next-line no-nested-ternary
              res.statusCode >= 500
                ? 'error'
                : res.statusCode >= 400 ? 'warn' : 'info';
            const completeMeta = {
              event: 'request_complete',
              request_id: requestId,
              method: req.method,
              url: req.originalUrl,
              status_code: res.statusCode,
              response_time_ms: durationMs,
            };
            if (req.route && req.route.path) {
              completeMeta.route_path = req.route.path;
            }
            req.log[level](completeMeta, 'Request completed');
          });

          res.on('close', () => {
            if (res.writableEnded) {
              return;
            }
            req.log.warn(
              {
                event: 'request_aborted',
                request_id: requestId,
                method: req.method,
                url: req.originalUrl,
                response_time_ms: Date.now() - startTime,
              },
              'Request aborted',
            );
          });

          next();
        },
      );
    };
  },
};

const resolveLogger = () => {
  if (!isServer || !hasPino) return fallbackLogger;
  return pinoLogger;
};

const logger = resolveLogger();

export const createRequestLoggerMiddleware =
  logger.createRequestLoggerMiddleware || (() => (req, res, next) => next());

export { setLogConfig };

export default logger;
